import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

export const GET_QUALIFICATION_COUNTS_BASE_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/calendar/facilities`;

const qualificationCountsResponseSchema = z.record(z.string(), z.number());

export type QualificationCountsResponse = z.infer<typeof qualificationCountsResponseSchema>;

interface QualificationCountsQueryParams {
  facilityId: string;
  start: string;
  end: string;
  timezone: string;
  enabled?: boolean;
}

export function createQualificationCountsQueryOptions(params: QualificationCountsQueryParams) {
  const { facilityId, start, end, timezone, enabled = true } = params;
  const url = `${GET_QUALIFICATION_COUNTS_BASE_URL}/${facilityId}/qualification-counts`;

  return {
    queryKey: ["qualification-counts", facilityId, start, end, timezone] as const,
    queryFn: async () => {
      const response = await get({
        url,
        queryParams: { start, end, tmz: timezone },
        responseSchema: qualificationCountsResponseSchema,
      });
      return response.data;
    },
    enabled: enabled && Boolean(facilityId),
    staleTime: 60_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_QUALIFICATION_COUNTS_FAILURE,
    },
  };
}

interface UseGetQualificationCountsParams {
  facilityId: string;
  start: string;
  end: string;
  timezone: string;
  enabled?: boolean;
}

export function useGetQualificationCounts(params: UseGetQualificationCountsParams) {
  return useQuery(createQualificationCountsQueryOptions(params));
}
